import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { Ticket, CheckCircle2, ArrowRight } from "lucide-react";
import { PublicFooter, PrivacyConsent } from "@/components/PublicFooter";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "long" }) : "");

export default function Invitation() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const preset = new URLSearchParams(window.location.search).get("code") || "";
  const [code, setCode] = useState(preset);
  const [meeting, setMeeting] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const c = code.trim();
    if (c.length < 3) { setMeeting(null); return; }
    const t = setTimeout(() => {
      api.get(`/public/meeting/${encodeURIComponent(c)}`).then((r) => setMeeting(r.data)).catch(() => setMeeting(null));
    }, 350);
    return () => clearTimeout(t);
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await register(form.name, form.email, form.password, { meeting_code: code.trim() });
      const sit = data?.access?.situation;
      if (sit === "scheduled") toast.success(`Bienvenue ! Votre accès invité s'ouvrira le ${fmt(data.access.access_starts_at)}.`);
      else if (sit === "expired_to_public") toast.info("Votre fenêtre invité est passée, profitez de 14 jours d'essai gratuit.");
      else toast.success("Bienvenue ! Votre accès découverte est ouvert.");
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="invitation-page">
      <div className="hidden lg:flex flex-col justify-between p-14 relative overflow-hidden" style={{ background: "linear-gradient(150deg, #FFF6F3 0%, #FFEDE9 55%, #FFE2DC 100%)" }}>
        <BrandLogo />
        <div className="relative z-10 max-w-md">
          <div className="inline-flex items-center gap-2 bg-white/70 text-coral-dark text-xs font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full mb-6"><Ticket className="h-3.5 w-3.5" /> Invité d'une réunion APOLLO</div>
          <h1 className="font-display font-extrabold text-4xl leading-[1.1] mb-6 text-ink">3 jours offerts pour découvrir Pulse.</h1>
          <ul className="space-y-3 text-ink-light">
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Accès complet pendant 3 jours, sans carte bancaire.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Ensuite, 14 jours d'essai public automatique.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-coral shrink-0" /> Devenez Membre APOLLO : l'accès devient gratuit et illimité.</li>
          </ul>
        </div>
        <p className="text-ink-light/60 text-xs relative z-10">Pulse, <Link to="/apropos" className="hover:text-coral-dark">by APOLLO</Link></p>
        <div className="absolute -right-32 -bottom-32 w-[28rem] h-[28rem] rounded-full bg-coral/15 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><BrandLogo /></div>
          <h2 className="font-display font-bold text-3xl text-ink mb-1">Activer mon accès invité</h2>
          <p className="text-slate-500 text-sm mb-8">Saisissez le code communiqué lors de votre réunion.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Code de réunion</label>
              <input data-testid="invitation-code-input" required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white font-mono tracking-wider" placeholder="EX. APOLLO-PARIS-06" />
              {meeting && meeting.valid && (
                <p data-testid="meeting-info" className="text-xs text-green-700 mt-1.5">✓ {meeting.label || "Réunion"}{meeting.group ? ` · ${meeting.group}` : ""}, accès dès le {fmt(meeting.access_starts_at)}</p>
              )}
              {meeting && !meeting.valid && code.trim().length >= 3 && (
                <p className="text-xs text-amber-600 mt-1.5">Code inconnu, vous démarrerez en essai public 14 jours.</p>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Nom</label>
              <input data-testid="invitation-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="Gilles Martin" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">E-mail</label>
              <input data-testid="invitation-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="vous@exemple.fr" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Mot de passe</label>
              <input data-testid="invitation-password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full border border-slate-200 rounded-sm px-3.5 py-2.5 text-sm outline-none focus:ring-1 focus:ring-coral bg-white" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={busy} data-testid="invitation-submit-btn"
              className="w-full flex items-center justify-center gap-2 bg-coral-strong text-white py-3 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60">
              {busy ? "Patientez…" : <>Activer mon accès <ArrowRight className="h-4 w-4" /></>}
            </button>
            <PrivacyConsent />
          </form>
          <p className="text-sm text-slate-500 mt-6 text-center">
            Pas de code ? <button onClick={() => navigate("/login?register=1")} className="text-coral-dark font-semibold hover:underline">Essai public 14 jours</button>
          </p>
        </div>
      </div>
    </div>
    <PublicFooter />
    </>
  );
}
